// Module 08 (worker) — SimModel assembly (sim-worker.v1). init() builds the engine's SimModel from
// the reduced GTA graph (via topology.loadReduced — the only sanctioned reader/normalizer; folds
// corridor→line, fuel synonyms, UNK vTier) plus the catalog anchors as the Scenario set. The slack
// bus + import cap are grounded harness constants (see pdd/build-logs/07.6-scenarios.log.md).
// Boundary (sim-worker.v1): import only this module, `domain`, `topology`, `engine`, or npm.
import { asNodeId } from "../domain/index";
import type { Scenario } from "../domain/index";
import { loadReduced } from "../topology/index";
import type { ReducedSources } from "../topology/index";
import type { SimModel } from "../engine/index";
import type { CorridorCaps } from "../powerflow/index";
import intertieCaps from "../../data/intertie_caps.json";

// V4 cutover (2026-06-28): the live model is now the unified V4 supergraph in the OSM-canonical namespace, so
// the system reference is W32963410 — the SAME Cherrywood EHV hub, under its canonical OSM id (the V4 crosswalk
// maps the legacy Debug-3 SUB_32963410 -> W32963410). Still the stable EHV reference, not the Darlington stub.
export const SLACK_BUS_ID = "W32963410";
// GTA import-interface cap (Debug 3 dispatch semantics: nuclear must-run → import cap → gas back-fill).
export const IMPORT_CAP_MW = 6000;
// P12 (KS-DYN): Ontario operating reserve (MW) — the backfill the system can mobilise for a contingency.
// Research-grounded (IESO Market Rules Ch.5 Issue 42.0 §4.5: total OR = the largest 1st contingency + ½ the
// 2nd): the Most Severe Single Contingency ≈ 900 MW (one Darlington unit, ~878 MW net, per the 2019 IESO
// Operability Assessment) sets the 10-minute requirement; the 30-minute tranche adds ~½ the next contingency,
// so the full OR ≈ 1.25–1.4 GW. We use 1400 (the full mobilisable reserve over the hourly horizon). A capacity
// loss BEYOND this can't be backfilled fast enough ⇒ UFLS sheds the rest; a single ~880 MW unit (< reserve)
// absorbs cleanly, matching reality that the MSSC is covered.
export const RESERVE_MW = 1400;

// engine.v3 P5 (SCED): per-corridor import ATC for the six real GTA-directional corridors, grounded +
// cited in data/intertie_caps.json (IESO transmission-interface TTCs — Bruce-Milton, QFW, BLIP, TEC, and
// the FETT 5900 MW shared western gate). Keyed on Generator.importCorridor. The pooled IMPORT_CAP_MW stays
// the aggregate ceiling + the fallback when this is absent or an import unit lacks a corridor.
export const CORRIDOR_CAPS: CorridorCaps = {
  corridorCapsMw: intertieCaps.corridorCapsMw,
  sharedInterfaces: intertieCaps.sharedInterfaces?.map((s) => ({ id: s.id, capMw: s.capMw, corridors: s.corridors })),
} as CorridorCaps;

export interface ModelConfig {
  slackBusId?: string;
  importCapMw?: number;
  reserveMw?: number;
  corridorCaps?: CorridorCaps | null; // null ⇒ explicitly disable per-corridor caps (pooled only); undefined ⇒ default
}

export function buildSimModel(sources: ReducedSources, scenarios: Scenario[], config?: ModelConfig): SimModel {
  const g = loadReduced({ sources });
  const caps = config?.corridorCaps === null ? undefined : (config?.corridorCaps ?? CORRIDOR_CAPS);
  return {
    nodes: g.nodes,
    edges: g.edges,
    generators: g.generators,
    loads: g.loads,
    slack: asNodeId(config?.slackBusId ?? SLACK_BUS_ID),
    importCapMw: config?.importCapMw ?? IMPORT_CAP_MW,
    reserveMw: config?.reserveMw ?? RESERVE_MW, // P12: contingency operating reserve
    ...(caps !== undefined ? { corridorCaps: caps } : {}), // P5: per-corridor import ATC
    scenarios,
  };
}
